import type { WorkflowEvent } from './workflows'

function parseSseDataLine(line: string): unknown | null {
  const trimmed = line.trim()
  if (!trimmed.startsWith('data:')) {
    return null
  }
  const payload = trimmed.slice('data:'.length).trim()
  if (payload.length === 0) {
    return null
  }
  try {
    return JSON.parse(payload) as unknown
  } catch {
    return null
  }
}

export async function consumeWorkflowEventStream(params: {
  runId: string
  onEvent: (event: WorkflowEvent) => void
  signal?: AbortSignal
}): Promise<void> {
  const baseUrl = import.meta.env.VITE_API_BASE_URL ?? ''
  const response = await fetch(
    `${baseUrl}/workflows/${params.runId}/events/stream`,
    {
      method: 'GET',
      credentials: 'include',
      headers: {
        Accept: 'text/event-stream',
      },
      signal: params.signal,
    }
  )

  if (!response.ok) {
    throw new Error(`Workflow stream failed (${response.status})`)
  }
  if (response.body == null) {
    throw new Error('Workflow stream returned no body')
  }

  const reader = response.body.getReader()
  const decoder = new TextDecoder()
  let buffer = ''

  while (true) {
    const { done, value } = await reader.read()
    if (done) {
      break
    }
    buffer += decoder.decode(value, { stream: true })
    const lines = buffer.split('\n')
    buffer = lines.pop() ?? ''
    for (const line of lines) {
      const parsed = parseSseDataLine(line)
      if (parsed == null || typeof parsed !== 'object') {
        continue
      }
      const event = parsed as WorkflowEvent
      if (typeof event.type !== 'string') {
        continue
      }
      params.onEvent(event)
    }
  }
}
